import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://csl-brands.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * Reusable SEO / AEO / GEO head manager.
 *
 * Props let every page declare its own metadata while falling back to the
 * organisation-wide defaults. Pass `path` (e.g. "/contact") so the canonical
 * URL, Open Graph URL and hreflang alternates resolve to the right page.
 *
 * @param {string}  [title]        Page title (overrides the default site title)
 * @param {string}  [description]  Meta description (overrides the default)
 * @param {string}  [path]         Route path, e.g. "/talents" (default "/")
 * @param {string}  [image]        Absolute or root-relative OG image URL
 * @param {string}  [type]         Open Graph type (default "website")
 * @param {object}  [structuredData] Extra JSON-LD node(s) to append to the graph
 * @param {boolean} [includeOrganization] Include the org/website graph (default true)
 * @param {object}  [alternates]    Localized fr, en and xDefault route paths
 */
const SEO = ({
    title,
    description,
    path = '/',
    image,
    keywords,
    type = 'website',
    structuredData,
    includeOrganization = true,
    alternates,
}) => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language || 'en';

    const pageTitle = title || t('seo.title');
    const pageDescription = description || t('seo.description');
    const pageKeywords = keywords || t('seo.keywords', { defaultValue: '' });
    const logoUrl = `${SITE_URL}/favicon.svg`;

    // Normalise the canonical URL for the current route.
    const cleanPath = path === '/' ? '' : `/${path.replace(/^\/+/, '')}`;
    const canonicalUrl = `${SITE_URL}${cleanPath}`;
    const resolveAlternate = (alternatePath) => (
        alternatePath.startsWith('http')
            ? alternatePath
            : `${SITE_URL}${alternatePath.startsWith('/') ? '' : '/'}${alternatePath}`
    );
    const alternateUrls = alternates
        ? {
            fr: resolveAlternate(alternates.fr),
            en: resolveAlternate(alternates.en),
            xDefault: resolveAlternate(alternates.xDefault),
        }
        : {
            fr: `${canonicalUrl}?lng=fr`,
            en: `${canonicalUrl}?lng=en`,
            xDefault: canonicalUrl,
        };

    // Resolve the social image to an absolute URL.
    const resolvedImage = !image
        ? DEFAULT_IMAGE
        : image.startsWith('http')
            ? image
            : `${SITE_URL}${image.startsWith('/') ? '' : '/'}${image}`;

    // Base JSON-LD graph (Organization + WebSite) shared across the whole site.
    const baseGraph = includeOrganization
        ? [
            {
                '@type': 'Organization',
                '@id': `${SITE_URL}/#organization`,
                name: 'CSL Brands',
                url: SITE_URL,
                logo: {
                    '@type': 'ImageObject',
                    url: logoUrl,
                },
                description: t('seo.description'),
                address: {
                    '@type': 'PostalAddress',
                    addressLocality: 'Douala',
                    addressCountry: 'CM',
                    streetAddress: 'Ndogbong, Citadelle',
                },
                contactPoint: {
                    '@type': 'ContactPoint',
                    telephone: '+237-653-45-96-38',
                    email: 'contact@csl-brands.com',
                    contactType: 'customer service',
                    areaServed: ['CM', 'CG'],
                    availableLanguage: ['en', 'fr'],
                },
                sameAs: [
                    'https://www.linkedin.com/company/csl-brands',
                    'https://twitter.com/cslbrands',
                    'https://facebook.com/cslbrands',
                ],
            },
            {
                '@type': 'WebSite',
                '@id': `${SITE_URL}/#website`,
                url: SITE_URL,
                name: 'CSL Brands',
                description: t('seo.description'),
                publisher: { '@id': `${SITE_URL}/#organization` },
                inLanguage: currentLang,
            },
        ]
        : [];

    // Allow callers to append one node or an array of nodes.
    const extraNodes = structuredData
        ? Array.isArray(structuredData)
            ? structuredData
            : [structuredData]
        : [];

    const graph = [...baseGraph, ...extraNodes];

    return (
        <Helmet>
            <html lang={currentLang} />
            <title>{pageTitle}</title>
            <meta name="title" content={pageTitle} />
            <meta name="description" content={pageDescription} />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <meta name="theme-color" content="#1C692F" />
            {pageKeywords ? <meta name="keywords" content={pageKeywords} /> : null}

            {/* Canonical */}
            <link rel="canonical" href={canonicalUrl} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="CSL Brands" />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDescription} />
            <meta property="og:image" content={resolvedImage} />
            <meta property="og:image:alt" content={pageTitle} />
            <meta property="og:locale" content={currentLang === 'fr' ? 'fr_FR' : 'en_US'} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDescription} />
            <meta name="twitter:image" content={resolvedImage} />
            <meta name="twitter:image:alt" content={pageTitle} />

            {/* Alternate Languages */}
            <link rel="alternate" href={alternateUrls.fr} hrefLang="fr" />
            <link rel="alternate" href={alternateUrls.en} hrefLang="en" />
            <link rel="alternate" href={alternateUrls.xDefault} hrefLang="x-default" />

            {/* Structured Data (AEO / GEO) */}
            {graph.length > 0 && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@graph': graph,
                    })}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
